export const authEndpoints = {
  register: "/auth/register",
  registerOtp: "/auth/register/otp",
  login: "/auth/login",
  phoneLoginStart: "/auth/phone/start",
  phoneLoginVerify: "/auth/phone/verify",
  me: "/auth/me",
  meContext: "/me/context",
  forgot: "/auth/forgot",
  reset: "/auth/reset",
  checkResetToken: (token: string) => `/auth/reset/${token}`,
} as const;

export const billingEndpoints = {
  plans: "/billing/plans",
  contact: "/billing/contact",
  status: "/billing/status",
  checkout: "/billing/checkout",
  portal: "/billing/portal",
  addons: "/billing/addons",
  addonsPreview: "/billing/addons/preview",
  checkoutStatus: (sessionId: string) => `/billing/checkout/status/${sessionId}`,
} as const;

export const invitationEndpoints = {
  inviteOrg: "/invitations/org",
  inviteDeal: "/invitations/deal",
  list: "/invitations",
  revoke: (invitationId: string) => `/invitations/${invitationId}/revoke`,
  resend: (invitationId: string) => `/invitations/${invitationId}/resend`,
  byToken: (token: string) => `/invitations/${token}`,
  accept: (token: string) => `/invitations/${token}/accept`,
} as const;

export const filesEndpoints = {
  uploadImage: "/uploads/image",
  uploadDocument: "/uploads/document",
  documentDownloadToken: (docId: string) => `/documents/${docId}/download-token`,
} as const;

export const dealEndpoints = {
  deals: "/deals",
  /** The rooms this account was *invited* into, as opposed to the ones it runs. */
  myDeals: "/me/deals",
  dealsExport: "/deals/export",
  dealStats: (dealId: string) => `/deals/${dealId}/stats`,
  dealById: (dealId: string) => `/deals/${dealId}`,
  dealClientSummary: (dealId: string) => `/deals/${dealId}/client-summary`,
  archiveDeal: (dealId: string) => `/deals/${dealId}/archive`,
  cancelDeal: (dealId: string) => `/deals/${dealId}/cancel`,
  unarchiveDeal: (dealId: string) => `/deals/${dealId}/unarchive`,
  dealGallery: (dealId: string) => `/deals/${dealId}/gallery`,
  dealParticipants: (dealId: string) => `/deals/${dealId}/participants`,
  dealTasks: (dealId: string) => `/deals/${dealId}/tasks`,
  taskById: (taskId: string) => `/tasks/${taskId}`,
  taskNudge: (dealId: string, taskId: string) => `/deals/${dealId}/tasks/${taskId}/nudge`,
  dealDocuments: (dealId: string) => `/deals/${dealId}/documents`,
  documentById: (docId: string) => `/documents/${docId}`,
  dealActivity: (dealId: string) => `/deals/${dealId}/activity`,
  dealEmails: (dealId: string) => `/deals/${dealId}/emails`,
  dealAppointments: (dealId: string) => `/deals/${dealId}/appointments`,
  appointmentAttendees: (dealId: string) => `/deals/${dealId}/appointment-attendees`,
  appointment: (appointmentId: string) => `/appointments/${appointmentId}`,
  appointmentCancel: (appointmentId: string) => `/appointments/${appointmentId}/cancel`,
  publicDealBySlug: (slug: string) => `/public/deals/${slug}`,
  shortLink: (code: string) => `/public/u/${code}`,
} as const;

export const ratingsEndpoints = {
  dealRatings: (dealId: string) => `/deals/${dealId}/ratings`,
  publishRating: (ratingId: string) => `/ratings/${ratingId}/publish`,
} as const;

export const asksEndpoints = {
  dealAsks: (dealId: string) => `/deals/${dealId}/asks`,
  dealUpdates: (dealId: string) => `/deals/${dealId}/updates`,
  replyAsk: (askId: string) => `/asks/${askId}/reply`,
  closeAsk: (askId: string) => `/asks/${askId}/close`,
} as const;

export const chatEndpoints = {
  threads: (dealId: string) => `/deals/${dealId}/chat/threads`,
  messages: (dealId: string, threadId: string) => `/deals/${dealId}/chat/threads/${threadId}/messages`,
  read: (dealId: string, threadId: string) => `/deals/${dealId}/chat/threads/${threadId}/read`,
  inbox: "/me/chat/inbox",
  socket: "/chat/ws",
} as const;

export const overviewEndpoints = {
  stats: "/overview/stats",
  requiresAttention: "/overview/requires-attention",
  activity: "/overview/activity",
  export: "/overview/export",
} as const;

export const teamEndpoints = {
  internal: "/team/internal",
  external: "/team/external",
} as const;

export const reportEndpoints = {
  summary: "/reports/summary",
  organization: "/reports/organization",
  projectedCompensation: "/reports/projected-compensation",
} as const;

export const compensationEndpoints = {
  deal: (dealId: string) => `/deals/${dealId}/compensation`,
} as const;

export const notificationEndpoints = {
  list: "/notifications",
  markRead: "/notifications/mark-read",
} as const;

export const settingsEndpoints = {
  myProfile: "/me/profile",
  phoneVerifyStart: "/me/phone/verify/start",
  phoneVerifyConfirm: "/me/phone/verify/confirm",
  phoneLogin: "/me/phone/login",
  orgProfile: (orgId: string) => `/organizations/${orgId}/profile`,
  changePassword: "/auth/change-password",
  deleteAccount: "/auth/me",
  deleteCheck: "/auth/me/delete-check",
  loginHistory: "/auth/login-history",
  statusTemplates: (orgId: string) => `/organizations/${orgId}/status-templates`,
  statusTemplateDefault: (orgId: string, slot: string) =>
    `/organizations/${orgId}/status-templates/defaults/${slot}`,
  statusSnippets: (orgId: string) => `/organizations/${orgId}/status-templates/snippets`,
  statusSnippet: (orgId: string, id: string) =>
    `/organizations/${orgId}/status-templates/snippets/${id}`,
} as const;

export const helpEndpoints = {
  articles: "/help/articles",
} as const;

export const auditEndpoints = {
  audit: "/audit",
} as const;

export const aiEndpoints = {
  listingDescription: "/ai/listing-description",
} as const;

export const searchEndpoints = {
  search: "/search",
} as const;
